import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import './TornadoDialog.css';
import { ReactComponent as CloseIcon } from '../../assets/img/controls/close.svg';

interface TornadoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  marker: L.Marker;
  children: React.ReactNode;
}

const TornadoDialog: React.FC<TornadoDialogProps> = ({
  isOpen,
  onClose,
  marker,
  children,
}) => {
  const map = useMap();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  const initialPosition = useMemo(() => {
    if (!marker || !map) return { x: 0, y: 0 };

    const markerPoint = map.latLngToContainerPoint(marker.getLatLng());
    // Start with default position - will be updated on render
    return { x: markerPoint.x + 30, y: markerPoint.y - 100 };
  }, [marker, map]);

  const [position, setPosition] = useState(initialPosition);

  const checkIfMobile = useCallback(() => {
    return window.innerWidth <= 640;
  }, []);

  const calculateOptimalPosition = useCallback(() => {
    if (!marker || !dialogRef.current || !map) return;

    const mobile = checkIfMobile();
    setIsMobile(mobile);

    if (mobile) {
      const mapWidth = map.getContainer().clientWidth;
      const mapHeight = map.getContainer().clientHeight;
      const dialogWidth = dialogRef.current.offsetWidth || 0;
      const dialogHeight = dialogRef.current.offsetHeight || 0;

      const x = (mapWidth - dialogWidth) / 2;
      const y = ((mapHeight - dialogHeight) / 2) - 50;

      setPosition({ x, y });
    } else {
      const markerPoint = map.latLngToContainerPoint(marker.getLatLng());
  
      const dialogWidth = dialogRef.current.offsetWidth || 0;
      const dialogHeight = dialogRef.current.offsetHeight || 0;
  
      const mapWidth = map.getContainer().clientWidth;
      const mapHeight = map.getContainer().clientHeight;
  
      // Default position -> right side
      let x = markerPoint.x + 30; // 30px offset from marker
      let y = markerPoint.y - (dialogHeight / 2);
  
      // Check to see if it moves out of the screen on right
      if (x + dialogWidth > mapWidth) {
        x = markerPoint.x - dialogWidth - 15;
      }
  
      // Check if dialog moves out of the screen vertically
      if (y < 10) {
        y = 10;
      } else if (y + dialogHeight > mapHeight - 10) {
        y = mapHeight - dialogHeight - 10;
      }
  
      setPosition({ x, y });
    }

  }, [marker, map, checkIfMobile]);


  useEffect(() => {
    if (!isOpen || !marker || !dialogRef.current) return;

    calculateOptimalPosition();
    
    map.on('move', calculateOptimalPosition);
    map.on('zoom', calculateOptimalPosition);
    map.on('resize', calculateOptimalPosition);

    window.addEventListener('resize', calculateOptimalPosition);
    
    return () => {
      map.off('move', calculateOptimalPosition);
      map.off('zoom', calculateOptimalPosition);
      map.off('resize', calculateOptimalPosition);
      window.removeEventListener('resize', calculateOptimalPosition);
    };
  }, [map, marker, isOpen, calculateOptimalPosition]);

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      className={`tornado-dialog ${isMobile ? 'tornado-dialog-mobile' : ''}`}
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="tornado-dialog-header">
        <h2 className="tornado-dialog-header_title">TORNADO</h2>
        <button
          onClick={onClose}
          className="tornado-dialog-header_close-button"
          aria-label="Close dialog"
        >
          <CloseIcon className="icon" />
        </button>
      </div>
      <div className="tornado-dialog-content">
        {children}
      </div>
    </div>
  );
};

export default TornadoDialog;